// Shared membership-tier helpers consumed by both BillingPanel (the
// authoritative billing UI) and lighter-weight upsell surfaces such as
// the AppLive Publish card. We keep the price-ID detection inside
// BillingPanel since it depends on env vars; everything that doesn't
// need price IDs lives here so other parts of the app can reason about
// tiers without re-implementing the rules.
//
// Why this lives in Lexappstore: that's where the existing pricing.js
// is, which is the closest neighbour for "anything related to plan
// metadata".

// Key used by BillingPanel's `handleMembershipBuy` to remember which
// tier the user most recently attempted to buy. We re-use it here as a
// fallback so the AppLive Publish card can still show the right tier
// name even when BillingPanel hasn't been mounted yet (i.e. we don't
// have access to the priceId-based detection it does). The string
// value MUST stay in sync with the one BillingPanel writes; updating
// it here without updating BillingPanel would silently break the
// fallback path for already-deployed users.
export const MEMBERSHIP_LAST_INTENT_KEY = 'lex:membership:last-plan-intent';

// How many apps each tier is allowed to publish. Numbers come from the
// product spec confirmed by the team:
//   FREE x1, SUPER x2, CREATOR x3, PRO x3, BUSINESS x10.
// ENTERPRISE remains unlimited/custom.
export const MEMBERSHIP_APP_LIMITS = Object.freeze({
  free: 1,
  super: 2,
  creator: 3,
  pro: 3,
  business: 10,
  enterprise: Number.POSITIVE_INFINITY,
});

// Display metadata for each tier. Keep in sync with BillingPanel's
// `membershipPlanRail`. Used by upsell surfaces to render plan names
// without asking BillingPanel for them.
export const MEMBERSHIP_PLAN_META = Object.freeze({
  free: { key: 'free', name: 'FREE', subtitle: 'App User', monthlyUsd: 0 },
  super: { key: 'super', name: 'SUPER', subtitle: 'Super App User', monthlyUsd: 19 },
  creator: { key: 'creator', name: 'CREATOR', subtitle: 'App Maker', monthlyUsd: 39 },
  pro: { key: 'pro', name: 'PRO', subtitle: 'App Maker Pro', monthlyUsd: 89 },
  business: { key: 'business', name: 'BUSINESS', subtitle: 'More Business', monthlyUsd: 399 },
  enterprise: { key: 'enterprise', name: 'ENTERPRISE', subtitle: 'Enterprise Scale', monthlyUsd: 1999 },
});

// "Recommended next tier" picker. The naive answer is just to bump the
// user up one notch, but the spec calls for skipping tiers that don't
// solve the user's pain point at this moment (publishing more apps).
//
// Concretely:
//   FREE                   → recommend SUPER   (up to 2 apps)
//   SUPER                  → recommend CREATOR (up to 3 apps + Profile Visibility Boost)
//   CREATOR                → recommend PRO     (Business Growth + visibility boost)
//   PRO                    → recommend BUSINESS (up to 10 apps + 10x boosts)
//   BUSINESS               → recommend ENTERPRISE (custom scale/support)
//   ENTERPRISE             → null (top tier, no upsell).
//
// This keeps the upsell honest: when we say "Upgrade to publish more
// apps" we always actually deliver more apps, and when we run out of
// app-slot upgrades we switch to selling the next tier's other perks.
export function getRecommendedUpgrade(currentKey) {
  const key = String(currentKey || 'free').trim().toLowerCase();
  switch (key) {
    case 'free':
      return 'super';
    case 'super':
      return 'creator';
    case 'creator':
      return 'pro';
    case 'pro':
      return 'business';
    case 'business':
      return 'enterprise';
    case 'enterprise':
    default:
      // 'enterprise' is the cap; unknown keys also map to null so the
      // caller knows not to render an upsell button.
      return key === 'enterprise' ? null : 'creator';
  }
}

// Best-effort detection of the user's current membership tier without
// requiring the env-var-driven priceId table BillingPanel uses.
//
// Resolution order:
//   1. If the user has an active vipPass subscription, try the
//      localStorage "last buy intent" hint that BillingPanel writes
//      whenever it kicks off a Stripe checkout.
//   2. If we can't find an explicit hint, fall back to 'super' — same
//      conservative default BillingPanel uses for active VIP users
//      whose priceId we can't classify.
//   3. Otherwise: 'free'.
//
// This is good enough for upsell copy. The authoritative source of
// truth is still BillingPanel (which checks priceIds against
// environment-specific MEMBERSHIP_PRICE_IDS); call sites that need
// strict accuracy should use that instead.
// 2026-05-06 (A2): 3-day VIP grace period. After Stripe marks a sub
// `canceled` / `unpaid`, we still consider VIP "active" for 3 days so
// the user has a friendly window to re-subscribe without losing access
// mid-feature. The grace window is bounded by `current_period_end`
// converted to ms — i.e. ends 3 days AFTER the period end, not 3 days
// after Stripe's status flip.
export const VIP_GRACE_PERIOD_MS = 3 * 24 * 60 * 60 * 1000;

function isWithinVipGrace(vip) {
  if (!vip) return false;
  const periodEndSec = Number(vip.current_period_end || vip.currentPeriodEnd || 0);
  if (!Number.isFinite(periodEndSec) || periodEndSec <= 0) return false;
  const periodEndMs = periodEndSec * 1000;
  return Date.now() < periodEndMs + VIP_GRACE_PERIOD_MS;
}

export function getMembershipPlanKeyFromUser(user) {
  const vip = user?.subscriptions?.vipPass || null;
  const isVipActive =
    vip &&
    (vip.status === 'active' || vip.status === 'trialing') &&
    !vip.cancel_at_period_end;
  // A2: even if vip is no longer "active", honour 3 days of grace based
  // on the previous period_end so the user keeps access while Stripe's
  // dunning / re-auth flows finish.
  const isInGrace = !isVipActive && isWithinVipGrace(vip);
  if (!isVipActive && !isInGrace) return 'free';

  if (typeof window !== 'undefined') {
    try {
      const remembered = String(
        window.localStorage.getItem(MEMBERSHIP_LAST_INTENT_KEY) || ''
      )
        .trim()
        .toLowerCase();
      if (remembered && MEMBERSHIP_APP_LIMITS[remembered] !== undefined) {
        return remembered;
      }
    } catch (_) {
      // localStorage unavailable (private mode etc.) — fall through.
    }
  }
  return 'super';
}

// Convenience: returns the app-publish limit for a given plan key.
// Unknown keys collapse to the free limit so we never accidentally
// hand out unlimited slots.
export function getAppLimitForPlan(planKey) {
  const key = String(planKey || 'free').trim().toLowerCase();
  return MEMBERSHIP_APP_LIMITS[key] ?? MEMBERSHIP_APP_LIMITS.free;
}
